import { catchError } from "@opendash/core";
import { $parse } from "@opendash/plugin-parse";
import { _Role } from "@opendash/plugin-parse/dist/types-generated";
import Parse from "parse";
import { useEffect, useState } from "react";
import { StepsExecuteFormValues, StepsObjectInScheduleExecution } from "..";
import {
  Schedule_Execution,
  Schedule_Execution_Step,
} from "../../../js/parsegenerated";

/**
 * This function filters the steps based on the roles of the user and sets the disabled property of the step object.
 * This is primarily for UI purposes.
 *
 * @param schedule - Schedule_Execution object
 * @returns steps array with disabled property set
 */
export function useFilteredStepsByRoles(schedule: Schedule_Execution) {
  const [steps, setSteps] = useState<StepsObjectInScheduleExecution[]>([]);
  const [stepsUpdated, setStepsUpdated] = useState<boolean>(false);

  useEffect(() => {
    setStepsUpdated(!stepsUpdated);
  }, [steps]);

  useEffect(() => {
    async function fetch() {
      // Get all Schedule_Execution_Steps with scheduleexecution pointer to props.schedule
      const [stepserror, stepsresult] = await catchError(
        new Parse.Query(Schedule_Execution_Step)
          .equalTo("scheduleexecution", schedule)
          .find()
      );

      const targetsteps: Array<StepsObjectInScheduleExecution> = [];

      for await (const step of stepsresult) {
        const [steproleserror, steproles] = await catchError(
          step.roles.query().find()
        );

        const [mediaerror, media] = await catchError(step.media.query().find());

        //Check if userroles and steproles have any common element (based on name)
        const commonroles = $parse.ui.roles.filter((role) =>
          steproles.some((steprole) => role === steprole.get("name"))
        );

        const addedstep: StepsObjectInScheduleExecution = {
          id: step.id,
          createdAt: step.get("createdAt"),
          updatedAt: step.get("updatedAt"),
          description: step.get("description"),
          tenant: step.get("tenant"),
          done: step.get("done"),
          fields: step.get("fields"),
          comment: step.get("comment"),
          scheduleexecution: step.get("scheduleexecution"),
          title: step.get("title"),
          media: media,
          roles: steproles as _Role[],
          disabled: commonroles.length === 0,
        };

        targetsteps.push(addedstep);
      }

      setSteps(targetsteps);
    }

    if (schedule === undefined) return;

    fetch();
  }, [schedule]);

  const updateStep = async (stepid: string, values: StepsExecuteFormValues) => {
    const stepToUpdate = steps.find((s) => s.id === stepid);
    if (stepToUpdate) {
      stepToUpdate.done = values.done;
      stepToUpdate.comment = values.comment;
      setSteps([...steps]);
    }
  };

  return [steps, updateStep, stepsUpdated] as [
    StepsObjectInScheduleExecution[],
    (stepid: string, values: StepsExecuteFormValues) => Promise<void>,
    boolean,
  ];
}
